import type { fabric } from 'fabric';
import type { History } from '../editor/history';
import { shortText } from '../print/rules';
import { isThaiText } from '../thai/ThaiTextbox';
import {
  addSlot, checkSlotText, clearSlot, clipLength, emptySlots, fmtTime, isEmptySlot, newId, setSlotInfo, SLOT_DEFAULT_SECONDS,
  SLOT_HINT_MAX, SLOT_LABEL_MAX, timingOf, totalLength, type OverlayTiming
} from '../video/project';
import { $, $in } from './dom';
import { isOpen, openSheet } from './sheets';
import { toast } from './toast';
import type { VideoCtx } from './videoView';

/*
 * เทมเพลตวิดีโอ (แบบ CapCut), on the video screen:
 *   "ช่องคลิป" (shVSlots)  every clip's place: name + hint, length, what is in it. Choose many files at once for the
 *                          empty slots in order, or one file for one slot ("เลือกไฟล์"/"เปลี่ยน"), empty a slot ("ล้าง"),
 *                          add an empty slot, and (admin) publish the work as a template — its clips become empty slots.
 *   "แก้ข้อความ" (shVTexts) every text over the video in a list, edited without the design screen.
 */

export interface SlotsHooks {
  history: History;
  fillSlots: (files: File[]) => Promise<void>;
  fillOne: (file: File, slotId: string) => Promise<void>;
  thumbOf: (media: string) => string;
  /** the person may publish templates (admin) */
  canPublish: () => boolean;
  publish: () => void;
  /** the work changed outside a commit (text edits): redraw the video screen */
  refresh: () => void;
}

export function initVideoSlots(ctx: VideoCtx, h: SlotsHooks) {
  const list = $('vlList'), multi = $<HTMLInputElement>('vSlotFiles'), one = $<HTMLInputElement>('vSlotFile');
  const say = (t: string) => { $('vlMsg').textContent = t; $('vlMsg').hidden = !t; };
  let target: string | null = null;   // slot id a single file is for

  function editable(): boolean {
    if (ctx.readonly()) { toast('งานนี้ดูอย่างเดียว ทำสำเนาเป็นของคุณก่อนจึงจะแก้ได้', true); return false; }
    return !!ctx.project();
  }

  /* ---------- ช่องคลิป ---------- */
  function paint() {
    const p = ctx.project(); if (!p) return;
    const ro = ctx.readonly();
    const empty = emptySlots(p), slots = p.clips.filter(c => c.slot).length;
    $('vlSummary').textContent = !p.clips.length
      ? 'ยังไม่มีคลิปหรือช่อง เพิ่มช่องว่างด้านล่าง (สำหรับทำเทมเพลต) หรือกด "+ คลิป" ที่หน้าวิดีโอ'
      : empty
        ? `เติมแล้ว ${p.clips.length - empty} จาก ${p.clips.length} ช่อง · ต้องใช้คลิปหรือรูปอีก ${empty} ไฟล์ · ยาวรวม ${fmtTime(totalLength(p))}`
        : `ครบทุกช่องแล้ว (${p.clips.length}) · ยาวรวม ${fmtTime(totalLength(p))}${slots ? '' : ' · ตั้งชื่อช่องไว้ได้ ถ้าจะทำเป็นเทมเพลต'}`;
    ($('vlMulti') as HTMLButtonElement).disabled = ro || !empty;
    $('vlMulti').textContent = empty ? `เลือกคลิปและรูป ${empty} ไฟล์ ใส่ช่องว่างตามลำดับ` : 'ไม่มีช่องว่าง';
    list.textContent = '';
    p.clips.forEach((c, k) => {
      const li = document.createElement('li'); li.className = 'vlrow' + (isEmptySlot(c) ? ' vlempty' : '');
      const num = document.createElement('span'); num.className = 'vlnum'; num.textContent = String(k + 1);
      const th = isEmptySlot(c) ? '' : h.thumbOf(c.media);
      const pic: HTMLElement = th ? Object.assign(document.createElement('img'), { src: th, alt: '' }) : Object.assign(document.createElement('span'), { className: 'vlpic' });
      const body = document.createElement('div');
      const label = document.createElement('input');
      label.maxLength = SLOT_LABEL_MAX; label.placeholder = `ฉากที่ ${k + 1}`; label.value = c.slot?.label ?? '';
      label.setAttribute('aria-label', `ชื่อช่องที่ ${k + 1}`); label.autocomplete = 'off'; label.disabled = ro;
      const hint = document.createElement('input');
      hint.maxLength = SLOT_HINT_MAX; hint.placeholder = 'คำแนะนำการถ่าย เช่น ถ่ายป้ายร้านจากฝั่งถนน'; hint.value = c.slot?.hint ?? '';
      hint.setAttribute('aria-label', `คำแนะนำของช่องที่ ${k + 1}`); hint.autocomplete = 'off'; hint.disabled = ro;
      const save = () => {
        const p2 = ctx.project(); if (!p2 || !editable()) return;
        const i = p2.clips.findIndex(x => x.id === c.id);
        if (i < 0) { say('ไม่พบช่องนี้แล้ว (อาจถูกลบหรือเลิกทำไป)'); paint(); return; }
        const l = label.value.trim() || `ฉากที่ ${i + 1}`;
        const why = checkSlotText(l, hint.value);
        if (why) { say(why); return; }
        if (ctx.commit(setSlotInfo(p2, i, { label: l, hint: hint.value }))) say('');
      };
      label.onchange = save; hint.onchange = save;
      const status = document.createElement('small');
      status.textContent = isEmptySlot(c) ? `ว่าง · ${clipLength(c).toFixed(1)} วิ`
        : `${c.kind === 'photo' ? 'รูป' : 'คลิป'} ${shortText(c.name, 24)} · ${clipLength(c).toFixed(1)} วิ`
          + (c.hold > 0.05 ? ` · ภาพค้าง ${c.hold.toFixed(1)} วิ` : '') + (ctx.missing().has(c.media) ? ' · ไม่อยู่ในเครื่องนี้' : '');
      if (!isEmptySlot(c) && ctx.missing().has(c.media)) status.className = 'err-text';
      const acts = document.createElement('div'); acts.className = 'addrow';
      const pick = document.createElement('button'); pick.className = 'btn'; pick.textContent = isEmptySlot(c) ? 'เลือกไฟล์' : 'เปลี่ยน';
      pick.disabled = ro;
      pick.onclick = () => { if (!editable()) return; target = c.id; one.value = ''; one.click(); };
      const clear = document.createElement('button'); clear.className = 'btn'; clear.textContent = 'ล้าง'; clear.hidden = isEmptySlot(c); clear.disabled = ro;
      clear.onclick = () => {
        const p2 = ctx.project(); if (!p2 || !editable()) return;
        const i = p2.clips.findIndex(x => x.id === c.id);
        if (ctx.commit(clearSlot(p2, i))) toast(`ล้างช่องที่ ${i + 1} แล้ว (ไฟล์ในเครื่องยังอยู่ กดเลิกทำได้)`);
      };
      acts.append(pick, clear);
      body.append(label, hint, status, acts);
      li.append(num, pic, body);
      list.append(li);
    });
    $('vlTplRow').hidden = !h.canPublish() || ro;
    for (const id of ['vlAddLabel', 'vlAddHint', 'vlAddSec', 'vlAdd']) ($(id) as HTMLInputElement).disabled = ro;
  }

  function open() { say(''); paint(); openSheet('shVSlots'); }

  $('vlMulti').onclick = () => { if (!editable()) return; multi.value = ''; multi.click(); };
  multi.onchange = () => { const fs = [...(multi.files ?? [])]; if (fs.length) void h.fillSlots(fs); };
  one.onchange = () => { const f = one.files?.[0]; if (f && target) void h.fillOne(f, target); };
  $('vlAdd').onclick = () => {
    const p = ctx.project(); if (!p || !editable()) return;
    const sec = Number($in('vlAddSec').value);
    const label = $in('vlAddLabel').value.trim() || `ฉากที่ ${p.clips.length + 1}`;
    if (!Number.isFinite(sec) || sec <= 0) { say('ใส่ความยาวช่องเป็นวินาที เช่น 3 หรือ 2.5'); return; }
    if (ctx.commit(addSlot(p, newId(), sec, { label, hint: $in('vlAddHint').value }))) {
      $in('vlAddLabel').value = ''; $in('vlAddHint').value = '';
      say(''); toast(`เพิ่มช่อง "${label}" ${sec} วิ ไว้ท้ายไทม์ไลน์แล้ว`);
    }
  };
  $in('vlAddSec').value = String(SLOT_DEFAULT_SECONDS);
  $('vlTpl').onclick = () => h.publish();
  $('vlTexts').onclick = () => openTexts();

  /* ---------- แก้ข้อความ ---------- */
  const texts = () => ctx.ed.userObjects().filter(o => isThaiText(o) || typeof (o as { text?: unknown }).text === 'string');
  const sayT = (t: string) => { $('vtxMsg').textContent = t; $('vtxMsg').hidden = !t; };

  function paintTexts() {
    const p = ctx.project(); if (!p) return;
    const box = $('vtxList');
    box.textContent = '';
    const all = texts();
    if (!all.length) {
      box.append(Object.assign(document.createElement('p'), { className: 'empty', textContent: 'ยังไม่มีข้อความบนวิดีโอ กด "ข้อความ" ที่หน้าวิดีโอเพื่อใส่' }));
      return;
    }
    all.forEach((o, n) => {
      const t = timingOf(o as Partial<OverlayTiming>, totalLength(p));
      const row = document.createElement('label'); row.className = 'vtxrow';
      const cap = document.createElement('span');
      cap.textContent = `ข้อความที่ ${n + 1} · ขึ้น ${fmtTime(t.vStart)}–${fmtTime(t.vEnd)}${o.visible === false ? ' · ซ่อนอยู่' : ''}`;
      const area = document.createElement('textarea');
      area.rows = 2; area.value = String((o as { text?: string }).text ?? ''); area.disabled = ctx.readonly();
      area.onchange = () => setText(o, area.value);
      row.append(cap, area);
      box.append(row);
    });
  }
  function setText(o: fabric.Object, value: string) {
    if (!editable()) { paintTexts(); return; }
    if (!value.trim()) { sayT('ข้อความว่างไม่ได้ ถ้าไม่ใช้แล้วให้ลบที่หน้าวิดีโอ (แตะแถบข้อความ → ลบ)'); paintTexts(); return; }
    const k = ctx.ed.userObjects().indexOf(o);
    if (k < 0) { sayT('ไม่พบข้อความนี้แล้ว (อาจถูกลบหรือเลิกทำไป)'); paintTexts(); return; }
    // styles per letter belong to the old text's positions: the new text gets the box's own style
    if (!h.history.setOnPages([{ page: 0, index: k }], { text: value, styles: {} })) { sayT('ยังแก้ไม่ได้ กำลังเปิดงานอยู่ รอสักครู่แล้วลองใหม่'); return; }
    sayT('');
    h.refresh();
  }
  function openTexts() { sayT(''); paintTexts(); openSheet('shVTexts'); }

  return {
    open, openTexts,
    /** after any change of the work */
    sync() { if (isOpen('shVSlots')) paint(); if (isOpen('shVTexts')) paintTexts(); }
  };
}

export type VideoSlots = ReturnType<typeof initVideoSlots>;
